import React from 'react'
// components
import CircularProgress from '@mui/material/CircularProgress'
// theme
import { colors } from 'themes'
// views
import { LoaderWrapper, StyledButton, Wrapper } from './views'
// types
import { ButtonProps } from '@mui/material'
// utils
import { darken } from 'polished'

export const BUTTON_LAYOUTS = {
  RED: 'RED',
  GRAY: 'GRAY',
  BLUE: 'BLUE',
  WHITE: 'WHITE',
  PURPLE: 'PURPLE',
  DISABLED: 'DISABLED',
  DARK_BLUE: 'DARK_BLUE',
  LIGHT_GRAY: 'LIGHT_GRAY',
  RED_INVERTED: 'RED_INVERTED',
  BLUE_INVERTED: 'BLUE_INVERTED',
  PURPLE_INVERTED: 'PURPLE_INVERTED'
}

const LAYOUT_SETTINGS = {
  [BUTTON_LAYOUTS.WHITE]: {
    textColor: colors.purple800,
    bg: colors.white100,
    border: `1px solid ${colors.white100}`,
    hoveredBg: darken(0.2, colors.white100)
  },
  [BUTTON_LAYOUTS.RED]: {
    bg: colors.red300,
    border: `1px solid ${colors.red300}`,
    hoveredBg: darken(0.1, colors.red300)
  },
  [BUTTON_LAYOUTS.LIGHT_GRAY]: {
    bg: colors.gray230,
    border: `1px solid ${colors.gray230}`,
    hoveredBg: darken(0.2, colors.gray230)
  },
  [BUTTON_LAYOUTS.GRAY]: {
    bg: colors.gray300,
    border: `1px solid ${colors.gray300}`,
    hoveredBg: darken(0.2, colors.gray300)
  },
  [BUTTON_LAYOUTS.BLUE]: {
    bg: colors.blue500,
    border: `1px solid ${colors.blue500}`,
    hoveredBg: darken(0.2, colors.blue500)
  },
  [BUTTON_LAYOUTS.PURPLE]: {
    bg: colors.purple300,
    border: `1px solid ${colors.purple300}`,
    hoveredBg: darken(0.2, colors.purple300)
  },
  [BUTTON_LAYOUTS.DARK_BLUE]: {
    bg: colors.darkBlue400,
    border: `1px solid ${colors.darkBlue400}`,
    textColor: colors.purple300,
    hoveredBg: darken(0.1, colors.darkBlue400)
  },
  [BUTTON_LAYOUTS.RED_INVERTED]: {
    bg: colors.white100,
    border: `1px solid ${colors.red300}`,
    textColor: colors.red300,
    hoveredBg: colors.white100
  },
  [BUTTON_LAYOUTS.PURPLE_INVERTED]: {
    bg: colors.white100,
    border: `1px solid ${colors.purple300}`,
    textColor: colors.purple300,
    hoveredBg: colors.white100
  },
  [BUTTON_LAYOUTS.BLUE_INVERTED]: {
    bg: colors.white100,
    border: `1px solid ${colors.blue500}`,
    textColor: colors.blue500,
    hoveredBg: colors.white100
  },
  [BUTTON_LAYOUTS.DISABLED]: {
    textColor: colors.gray900,
    bg: colors.gray280,
    border: `1px solid ${colors.gray280}`,
    cursor: 'not-allowed'
  }
}

export const CREATE_BUTTON_STYLE = {
  height: 40,
  width: 'max-content'
}

interface CustomStyleProps {
  bg?: string
  textColor?: string
  hoveredBg?: string
  shadowLess?: boolean
}

interface Props extends ButtonProps, CustomStyleProps {
  label?: string
  style?: React.CSSProperties
  layout?: keyof typeof LAYOUT_SETTINGS
  disabled?: boolean
  className?: string
  isLoading?: boolean
}

const Button: React.FC<Props> = ({
  label,
  style,
  layout = BUTTON_LAYOUTS.PURPLE,
  children,
  disabled,
  className,
  isLoading,
  ...props
}) => {
  const settings = isLoading || disabled ? LAYOUT_SETTINGS[BUTTON_LAYOUTS.DISABLED] : LAYOUT_SETTINGS[layout]

  return (
    <Wrapper className={className}>
      <StyledButton
        style={style}
        color='primary'
        variant='contained'
        {...settings}
        {...props}
        disabled={disabled || isLoading}>
        {children}
      </StyledButton>
      {isLoading && (
        <LoaderWrapper>
          <CircularProgress size={24} />
        </LoaderWrapper>
      )}
    </Wrapper>
  )
}

export default Button
